import './styles.scss';
import React, {Dispatch, useEffect, useState} from "react";
import {NavLink, useNavigate} from "react-router-dom";

/* Data */
import SEARCH_META_DATA from "../../../../assets/data/search.meta.json";

/* Types, Constants, Utils */
import Utils from "../../../../utils";
import * as Constants from "../../../../constants";

/* Bootstrap */
import {Collapse, Row, Col, Button} from "react-bootstrap";

/* Redux */
import {AnyAction} from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../../redux";
import {IResultsState} from "../../../../types/redux/result";
import {ISearchState} from "../../../../types/redux/search";

/* Hooks */
import useCardField, {ICardFieldContent, IUseCardField} from "../../hooks/useCardField";
import LinkButton from "../../../button-link";
import SearchUtils from "../../../../utils/search-utils";
import {setQuery} from "../../../../redux/slices/search-slice";
import {NavigateFunction} from "react-router/dist/lib/hooks";
import copy from "copy-to-clipboard";
import {addToast} from "../../../../redux/slices/toast-slice";

interface IProps  {
  record: any;
  setColumns: boolean;
  noFold: boolean;
  expandAll?:boolean;
}

interface IMetaFields {
  'above': JSX.Element[],
  'below': JSX.Element[]
}

const MetaFields = (props: IProps):JSX.Element => {
  const {
    record,
    setColumns,
    noFold,
    expandAll
  } = props;

  const resultsState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search);

  /* state */
  const [expanded, setExpanded] = useState<boolean>(resultsState.expand_all);
  const [metaFields, setMetaFields] = useState<IMetaFields>({'above': [], 'below': []})

  /* hooks */
  const {getFieldContent}: IUseCardField = useCardField();

  const dispatch:Dispatch<AnyAction> = useDispatch();
  const navigate: NavigateFunction = useNavigate();

  useEffect(()=>{
    setExpanded(expandAll)
  }, [expandAll]);

  useEffect(()=>{

    const entry = (id: string, label: string, value: any, loc: string): void => {
      if (value) {

        let elem: JSX.Element;
        if (setColumns) {
          const fieldContent: ICardFieldContent = getFieldContent(label.toLowerCase(), value, label);
          if ((loc === Constants.BELOW) && (fieldContent.selectionCount > 0)) {
            loc = Constants.ABOVE;
          }
          elem = <span className={`entry entry-${id} d-flex flex-row`}>
                  {(label.length > 0) && <Col lg={2} className={'text-normal'}><b>{label}:</b></Col>}
                  <Col lg={10} className={'ms-1 entry-text text-normal text-break'}>{fieldContent.elem}</Col>
                </span>
        } else {
          elem = <div>
            <div className={`entry entry-${id} max-lines lines-4 d-none d-lg-block`}>
              <span className={'pe-1 text-wrap text-break'}>
                {(label.length > 0) && <span className={'label me-1'}><strong>{label}:</strong></span>}
                {value}
              </span>
            </div>

            <Row className={`entry entry-${id} max-lines lines-4 d-lg-none`}>
              <Col className={'pe-1'}> <span className={'label me-1'}>
                {(label.length > 0) && <strong>{label}:</strong>}
              </span></Col>
              <Col className={'px-1'}>{value}</Col>
            </Row>
          </div>
        }

        fields[loc].push(elem);
        setMetaFields(fields);
      }
    }

    const fields: IMetaFields = {
      'above': [],
      'below': []
    }

    const copyLink = (url: string): void => {
      try {
        if (copy(url)) {
          dispatch(addToast({
            bg: 'bg-success',
            msg: Constants.LINK_COPIED,
            id: -1
          }))
        } else {
          console.log(Constants.UNABLE_TO_COPY)
        }
      } catch (e) {
        throw new Error(Constants.UNABLE_TO_COPY)
      }
    }

    const resultFields: any[] = SEARCH_META_DATA.resultFields;
      resultFields.forEach((field: any) =>  {
        for (const fieldId in field) {
          const fieldValues: string[] = field[fieldId];
          if (fieldValues && (fieldValues[0] === Constants.ALL)) {
            let loc: string = (fieldValues.indexOf(Constants.ABOVE) !== -1) ? Constants.ABOVE : Constants.BELOW;
            if (noFold) {
              loc = Constants.ABOVE;
            }

            /* special case - field is selected in search --> move to above the fold */
            let terms: string[] = searchState.query.filter(q => q.term !== null).map(q=> q.term)
            if (terms) {
              terms = terms.join(' ').split(' ');
            }

            const fieldSelected: boolean =  (terms.findIndex(t=>{
              const term: string = t.split(':')[0];
              if ((term.length > 1) && SEARCH_META_DATA.allFields_reverse[term]) {
                const termId: string = SEARCH_META_DATA.allFields_reverse[term][0];
                return fieldId.toLowerCase() === termId
              } else {
                return false
              }
            }) !== -1)
            if (fieldSelected) {
              loc = Constants.ABOVE;
            }

            /* @ts-ignore*/
            const mappedValues: string[] = SEARCH_META_DATA.allFields[fieldId];
            switch (fieldId) {

              case 'id':
                let value: string = record[fieldId];
                if (record.industry && (((record.industry.toLowerCase() === Constants.TOBACCO) || (record.industry.toLowerCase() === Constants.DRUG)) && (record['tid']))) {
                  value += " ( TID : "+ record["tid"] +" )";
                }
                entry(fieldId, mappedValues[0], value, loc)
                break;

              case 'access':
                if (record[fieldId]) {
                  const accessData: any = JSON.parse(record[fieldId]);
                  const link: JSX.Element = <NavLink
                    to={accessData.link}
                    className={'me-2 btn-link text-normal text-link-color cursor-pointer'}
                  >
                    {accessData.title + ', ' + accessData.type}
                  </NavLink>
                  entry(fieldId, Utils.toTitleCase(fieldId), link, loc)
                }
                break;

              case 'area':
              case 'folder':
              case 'box':
                if (record[fieldId] && (record[fieldId].toLowerCase() !== 'na')) {
                  const query: string = `q=null,all,contains,${fieldId}:"${record[fieldId]}"`;
                  const link: JSX.Element = <NavLink
                    to={`/tobacco/documents/?q=${query}`}
                    className={'me-2 btn-link text-normal text-link-color cursor-pointer'}
                    onClick={()=>dispatch(setQuery({query:SearchUtils.convertQueryParams(query), recent_query:true}))}
                  >
                    {record[fieldId]}
                  </NavLink>
                  entry(fieldId, Utils.toTitleCase(fieldId), link, loc)
                }
                break;

              case 'excerpt':
                const excerpt: string | undefined = (record?.highlighting && record?.highlighting.ot) ?
                  Utils.removeTags(record.highlighting.ot.join(', ')).replace(/�|�/g, "") : undefined;
                  entry(fieldId, 'Excerpt', excerpt, loc)
                break;

              case 'availability':
                if (record[fieldId]) {
                  entry(fieldId, 'Availability', record[fieldId].join('; '), loc)
                }
                break;

              case 'ark':
                entry(fieldId,'ARK', "ark:/88122/" + record.id, loc)
                break;

              case 'url':
                const purl:string = window.location.protocol + '//' + window.location.host + '/docs/'  + record.id + '/';
                const copyEntry: JSX.Element = <Button
                  className={'copy-link-btn btn-link text-smaller p-0 link-underline-none m-0'}
                  onClick={()=>copyLink(purl)}
                >
                  <i className={'bi bi-link me-2'}></i>
                  Copy Link
                </Button>
                if (setColumns) {
                  const elem: JSX.Element = <div>
                    <span>{purl} </span>
                    <span className={'ms-2'}>{copyEntry}</span>
                  </div>
                  entry(fieldId, 'URL', elem, loc)
                } else {
                  entry(fieldId, 'URL', purl, loc)
                  entry('copy-link','', copyEntry, loc)
                }

                break;

               case 'source':
                 try {
                   const json: any[] = JSON.parse(record[fieldId]);
                   entry(fieldId, 'Source', json[0].title, loc)
                 } catch (e) {}
                break;

              case 'related':
                if (record[fieldId]) {
                  let relatedLinks: JSX.Element = <div>
                    {record[fieldId].map((r:string, index: number) =>{
                      return <span
                        key={index}
                        className={'me-2 btn-link text-normal text-link-color cursor-pointer'}
                        onClick={()=>navigate('/' + Utils.convertToKeyOrId(record[Constants.INDUSTRY])+ '/documents/viewer/?id=' + r)}
                      >
                        {r}
                      </span>
                    })}
                  </div>
                  entry(fieldId, 'Related', relatedLinks, loc)
                }
                break;

              case 'dd':
              case 'au':
              case 'dt':
                entry(fieldId, mappedValues[0], record[fieldId] ? record[fieldId] : Constants.UNKNOWN, loc)
                break;

              case 'cited':
                if (record['journal']) {
                  let journal: any[] = JSON.parse(record['journal']);
                  entry(fieldId, mappedValues[0], <ol className={'ps-0'}>
                    {journal.map((entry:any, index:number) => {
                      let url: string = window.location.protocol + '//' + window.location.host + '/all-industries/bibliography/?industry=all-industries&db-set=bibliography&npp=20&q=null,all,contains,refid:' + entry.id + '&sort=relevance&pg=1';
                      return <li className={'p-0 m-0 cited'} key={'jrn'+index}>
                        <a href={url} target={'_self'} rel="noreferrer"><i>{getFieldContent(fieldId, entry.title, mappedValues[0]).elem}</i></a>
                      </li>
                    })}
                  </ol>, loc)
                }
                break;

              default:
                entry(fieldId, mappedValues[0], record[fieldId], loc)
            }
          }
        }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, searchState.query])

  const toggleExpanded = ():void =>{
    setExpanded(!expanded)
  }

  return (<div className={'meta-fields'}>
      <Row className={'doc-content mb-0 position-relative'}>
        {metaFields[Constants.ABOVE].map((elem: JSX.Element, index: any) =>
          <Row className={'entry-row d-flex'} key={index}>{elem}</Row>)}
      </Row>

      {(metaFields[Constants.BELOW].length > 0) &&
        <>
          <Collapse in={expanded}>
            <div className={'doc-content mb-2'}>
              <Row>
                {metaFields[Constants.BELOW].map((elem: JSX.Element, index: any) =>
                  <Row className={'entry-row d-flex'} key={index}>{elem}</Row>)}
              </Row>
            </div>
          </Collapse>

          <Row className={'expand'}>
            <Col className={'mb-2 mx-0 px-0'} >
              <LinkButton
                label={expanded ? 'Show Less' : 'Show More'}
                linkUpDownArrow={expanded ? Constants.UP : Constants.DOWN}
                clickFunc={toggleExpanded}
              />
            </Col>
          </Row>
        </>}
    </div>
  )
}

export default MetaFields;
